import type { INavigationRoute } from '@/components/sidebar/NavigationRoutes'
import { SINGLE_SCHEMA_DEPRECATED_ROUTE_NAMES } from '@/config/singleSchema'
import { isSingleSchemaEnabled } from '@/services/regionScope'

/** Fragmented list pages replaced by /our-vehicles and /our-drivers with query filters. */
const HIDDEN_ROUTE_NAMES = new Set<string>([
  ...SINGLE_SCHEMA_DEPRECATED_ROUTE_NAMES,
  'go-metro-vehicles',
  'aeversa-vehicles',
  'uber-deployed-drivers',
  'valt-v2-valt-vehicles',
  'valt-v2-valt-drivers',
  'valt-v2-uber-vehicles',
  'uteh-vehicles',
  'bikes',
])

/** Parent menus that become a single Vehicles link. */
const VEHICLE_MENU_PARENTS = new Set(['valt-v2-vehicles', 'fleet-vehicles-menu'])

/** Parent menus that become a single Drivers link (only when all children are driver-list entries). */
const DRIVER_MENU_PARENTS = new Set(['valt-v2-drivers'])

const DRIVER_LIST_CHILD_NAMES = new Set([
  'drivers',
  'our-drivers',
  'uber-deployed-drivers',
  'valt-v2-valt-drivers',
])

const VEHICLE_LIST_CHILD_NAMES = new Set([
  'vehicles',
  'our-vehicles',
  'go-metro-vehicles',
  'valt-v2-valt-vehicles',
  'valt-v2-uber-vehicles',
  'uteh-vehicles',
  'bikes',
  'aeversa-vehicles',
])

function isDriverListParent(route: INavigationRoute): boolean {
  if (DRIVER_MENU_PARENTS.has(route.name)) return true
  if (!route.children?.length) return false
  return route.children.every((c) => DRIVER_LIST_CHILD_NAMES.has(c.name))
}

function isVehicleListParent(route: INavigationRoute): boolean {
  if (VEHICLE_MENU_PARENTS.has(route.name)) return true
  if (!route.children?.length) return false
  return (
    route.children.some((c) => VEHICLE_LIST_CHILD_NAMES.has(c.name)) &&
    route.children.every((c) => VEHICLE_LIST_CHILD_NAMES.has(c.name) || c.name.startsWith('maintenance'))
  )
}

function toUnifiedDriversLink(route: INavigationRoute): INavigationRoute {
  return {
    name: 'our-drivers',
    displayName: 'Drivers',
    meta: { ...route.meta, icon: route.meta?.icon || 'moped' },
  }
}

function toUnifiedVehiclesLink(route: INavigationRoute): INavigationRoute {
  return {
    name: 'our-vehicles',
    displayName: 'Assets',
    meta: { ...route.meta, icon: route.meta?.icon || 'directions_car' },
  }
}

function dedupeSiblingLinks(children: INavigationRoute[]): INavigationRoute[] {
  const visible = children.filter((c) => !HIDDEN_ROUTE_NAMES.has(c.name))
  const names = new Set(visible.map((c) => c.name))
  const hasOurDrivers = names.has('our-drivers')
  const hasOurVehicles = names.has('our-vehicles')

  const isDriverAnalyticsEntry = (route: INavigationRoute) => /analytics/i.test(route.displayName)
  const isVehicleAnalyticsEntry = (route: INavigationRoute) => /analytics/i.test(route.displayName)

  return visible
    .filter((c) => !(hasOurDrivers && c.name === 'drivers' && !isDriverAnalyticsEntry(c)))
    .filter((c) => !(hasOurVehicles && c.name === 'vehicles' && !isVehicleAnalyticsEntry(c)))
    .filter((c) => !(hasOurVehicles && c.name === 'bikes'))
    .map((c) => {
      if (c.name === 'our-drivers') return { ...c, displayName: 'Drivers' }
      if (c.name === 'our-vehicles') return { ...c, displayName: 'Mobile Assets' }
      return c
    })
}

function consolidateRoute(route: INavigationRoute): INavigationRoute | null {
  if (HIDDEN_ROUTE_NAMES.has(route.name)) return null

  if (isDriverListParent(route)) {
    return toUnifiedDriversLink(route)
  }

  if (isVehicleListParent(route)) {
    return toUnifiedVehiclesLink(route)
  }

  if (!route.children?.length) {
    return route
  }

  const children = route.children
    .map((child) => consolidateRoute(child))
    .filter((child): child is INavigationRoute => child !== null)

  const deduped = dedupeSiblingLinks(children)

  if (deduped.length === 0) return null

  return { ...route, children: deduped }
}

/** Collapse fragmented vehicle/driver nav into single links with on-page filters. */
export function applySingleSchemaNavFilter<T extends { routes: INavigationRoute[] }>(config: T): T {
  if (!isSingleSchemaEnabled()) return config

  const routes = config.routes
    .map((route) => consolidateRoute(route))
    .filter((route): route is INavigationRoute => route !== null)

  return { ...config, routes }
}
